// Anchor React
import { useState } from 'react';

// ANCHOR Models
import createModel, { createSelectors, createValue } from 'react-scoped-model';

// ANCHOR Utils
import { tuple } from '@lyon/utils/tuple';
import { IFormsResult } from '@lyon/utils/api/results/forms';

interface IProps {
  initialData: IFormsResult;
}

export const UpdateFormForm = createModel(({
  initialData,
}: IProps) => {
  const [title, setTitle] = useState(initialData.title);
  const [type, setType] = useState(initialData.type);
  const [description, setDescription] = useState(initialData.description);
  const [scoringPolicy, setScoringPolicy] = useState(initialData.scoringPolicy);
  const [allowedAttempts, setAllowedAttempts] = useState(initialData.allowedAttempts);
  const [timeLimit, setTimeLimit] = useState(initialData.timeLimit);
  const [accessCode, setAccessCode] = useState(initialData.accessCode);
  const [canGoBack, setCanGoBack] = useState(initialData.canGoBack);
  const [isLocked, setIsLocked] = useState(initialData.isLocked);
  const [isPractice, setIsPractice] = useState(initialData.isPractice);
  const [oneTimeResult, setOneTimeResult] = useState(initialData.oneTimeResult);
  const [showCorrectResponse, setShowCorrectResponse] = useState(initialData.showCorrectResponse);
  const [showRankPercentile, setShowRankPercentile] = useState(initialData.showRankPercentile);
  const [showScore, setShowScore] = useState(initialData.showScore);
  const [showAfterRegrade, setShowAfterRegrade] = useState(initialData.showAfterRegrade);
  const [shuffleQuestion, setShuffleQuestion] = useState(initialData.shuffleQuestion);
  const [textSelection, setTextSelection] = useState(initialData.textSelection);

  return {
    state: {
      title,
      type,
      description,
      scoringPolicy,
      allowedAttempts,
      timeLimit,
      accessCode,
      canGoBack,
      isLocked,
      isPractice,
      oneTimeResult,
      showCorrectResponse,
      showRankPercentile,
      showScore,
      showAfterRegrade,
      shuffleQuestion,
      textSelection,
    },
    handler: {
      title: setTitle,
      type: setType,
      description: setDescription,
      scoringPolicy: setScoringPolicy,
      allowedAttempts: setAllowedAttempts,
      timeLimit: setTimeLimit,
      accessCode: setAccessCode,
      canGoBack: setCanGoBack,
      isLocked: setIsLocked,
      isPractice: setIsPractice,
      oneTimeResult: setOneTimeResult,
      showCorrectResponse: setShowCorrectResponse,
      showRankPercentile: setShowRankPercentile,
      showScore: setShowScore,
      showAfterRegrade: setShowAfterRegrade,
      shuffleQuestion: setShuffleQuestion,
      textSelection: setTextSelection,
    },
  };
}, {
  displayName: 'Models.UpdateFormForm',
});

export const useUpdateFormFormTitle = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.title,
    handler.title,
  ),
);

export const useUpdateFormFormDescription = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.description,
    handler.description,
  ),
);

export const useUpdateFormFormScoringPolicy = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.scoringPolicy,
    handler.scoringPolicy,
    state.allowedAttempts,
  ),
);

export const useUpdateFormFormAllowedAttempts = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.allowedAttempts,
    handler.allowedAttempts,
  ),
);

export const useUpdateFormFormTimeLimit = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.timeLimit,
    handler.timeLimit,
  ),
);

export const useUpdateFormFormAccessCode = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.accessCode,
    handler.accessCode,
  ),
);

export const useUpdateFormFormCanGoBack = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.canGoBack,
    handler.canGoBack,
  ),
);

export const useUpdateFormFormIsLocked = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.isLocked,
    handler.isLocked,
  ),
);

export const useUpdateFormFormIsPractice = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.isPractice,
    handler.isPractice,
  ),
);

export const useUpdateFormFormOneTimeResult = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.oneTimeResult,
    handler.oneTimeResult,
  ),
);

export const useUpdateFormFormShowCorrectResponse = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.showCorrectResponse,
    handler.showCorrectResponse,
  ),
);

export const useUpdateFormFormShowRankPercentile = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.showRankPercentile,
    handler.showRankPercentile,
  ),
);

export const useUpdateFormFormShowScore = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.showScore,
    handler.showScore,
  ),
);

export const useUpdateFormFormShowAfterRegrade = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.showAfterRegrade,
    handler.showAfterRegrade,
  ),
);

export const useUpdateFormFormShuffleQuestion = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.shuffleQuestion,
    handler.shuffleQuestion,
  ),
);

export const useUpdateFormFormTextSelection = createSelectors(
  UpdateFormForm,
  ({ state, handler }) => tuple(
    state.textSelection,
    handler.textSelection,
  ),
);

export const useUpdateFormFormSubmitPayload = createValue(
  UpdateFormForm,
);
